import React, { useContext, useMemo } from 'react';
import { Grid, TextField } from '@nextiva/next-ui-components';
import { Controller, useFormContext } from 'react-hook-form';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';

import { RevealCard } from 'components/RevealCard';
import { actionTypes, CallForwardContext } from 'containers/CallForwarding';
import { HuntGroupService } from 'services';

const ALWAYS_ENABLED = 'always.enabled';

export const CallForwardAlways = ({ huntGroupId = '' }) => {
  const { state, dispatch } = useContext(CallForwardContext);
  const {
    clearErrors,
    getValues,
    setError,
    setValue,
    control,
  } = useFormContext();

  const debouncedSetAlwaysDirty = useMemo(
    () =>
      debounce(
        (value) => {
          const dirty = value !== state.originalFields.always.phoneNumber;
          if (dirty === state.dirtySections.always) return; // avoid re-rendering if dirty is unchanged

          dispatch({
            type: actionTypes.DIRTY_UPDATED,
            payload: { sectionName: 'always', dirty },
          });
        },
        200,
        { trailing: true },
      ),
    [
      state.originalFields.always.phoneNumber,
      state.dirtySections.always,
      dispatch,
    ],
  );

  const debouncedUpdateAlwaysEnabled = useMemo(
    () =>
      debounce(
        async () => {
          const checked = getValues(ALWAYS_ENABLED);

          try {
            await HuntGroupService.toggleCallForwardAlwaysActive(
              huntGroupId,
              checked,
            );
            dispatch({
              type: actionTypes.SWITCH_TOGGLED,
              payload: { sectionName: 'always', enabled: checked },
            });
          } catch (error) {
            dispatch({
              type: actionTypes.SNACKBAR_SET,
              payload: {
                message:
                  'There was an error while updating the active status for Call Forwarding (Always). Please try again later or contact support if the issue persists.',
                type: 'error',
              },
            });

            setValue(ALWAYS_ENABLED, !checked, { shouldDirty: false });
          }
        },
        500,
        { trailing: true },
      ),
    [dispatch, getValues, huntGroupId, setValue],
  );

  const handleMissingFields = () => {
    const phoneNumber = getValues('always.phoneNumber');
    const payload = phoneNumber
      ? {
          message:
            'You have unsaved changes, click the blue save button to enable this feature.',
          section: 'always',
          type: 'error',
        }
      : {
          message:
            'Missing required information. Fill out the missing fields and then click the blue "Save" button to enable this feature.',
          section: 'always',
          type: 'error',
        };

    dispatch({ type: actionTypes.TOGGLE_FAILED, payload });
    if (!phoneNumber)
      setError(
        'always.phoneNumber',
        { type: 'required', message: 'Phone number is required.' },
        { shouldFocus: true },
      );
  };

  const handleSwitchClick = ({ event, field }) => {
    if (!state.originalFields.always.phoneNumber) {
      handleMissingFields();
      return;
    }

    field.onChange(event);
    debouncedUpdateAlwaysEnabled();
  };

  return (
    <RevealCard
      description="Automatically forward all your calls to a different phone number."
      handleSwitchClick={handleSwitchClick}
      sectionName="always"
      title="Call forward (always)"
      tooltip="Forward all the team's calls to a different phone number."
    >
      <Grid container>
        <Grid className="field-container" item xs={5}>
          <Controller
            control={control}
            defaultValue=""
            name="always.phoneNumber"
            render={({ field, fieldState }) => (
              <TextField
                fullWidth
                id="always-phone-number"
                label="PHONE NUMBER"
                inputProps={{ maxLength: 20 }}
                // eslint-disable-next-line react/jsx-no-duplicate-props
                InputProps={{ disableUnderline: true }}
                InputLabelProps={{ shrink: true }}
                {...field}
                onChange={(event) => {
                  if (fieldState.error?.type === 'required')
                    clearErrors('always.phoneNumber');
                  field.onChange(event);
                  debouncedSetAlwaysDirty(event.target.value);
                }}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                data-testid="PlatformAdminUI-TeamVoiceSettings-CallForwardAlways-PhoneNumber"
              />
            )}
          />
        </Grid>
      </Grid>
    </RevealCard>
  );
};

CallForwardAlways.propTypes = {
  huntGroupId: PropTypes.string.isRequired,
};
